/* ATHLO+ — Contour field: cursor-warped line background (ADDON, removable)
 * ==========================================================================
 * Canvas-2D field of fine vertical contour lines that drift on an ambient
 * sine field and bulge away from the cursor, brightening where they bend
 * (wodniack.dev-style hero fabric, redrawn in carbon + lime).
 *
 * Attaches to every <canvas data-hub-contour> and fills its PARENT element
 * (parent must be position:relative; canvas should be absolutely inset).
 * Loaded only by sections that render such a canvas (hub-finale.liquid).
 *
 * Perf/safety rails: DPR capped at 2 · rAF only while the canvas is on
 * screen (IntersectionObserver) · coarse pointers get ambient drift only ·
 * prefers-reduced-motion gets ONE static frame, no animation · adaptive
 * line density on small screens. Fails safe: no canvas 2D context (or any
 * error) leaves an empty transparent canvas — the section still reads fine.
 * Removable: delete this file + the section that loads it.
 * ========================================================================= */
(function () {
  'use strict';

  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var FINE = !!(window.matchMedia && window.matchMedia('(hover:hover) and (pointer:fine)').matches);

  function attach(canvas) {
    if (canvas.__hubContour) return;
    canvas.__hubContour = true;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    var wrap = canvas.parentElement;
    if (!wrap) return;

    var W = 0, H = 0, GAP = 26, STEP = 16, t = 0;
    var mx = -1e4, my = -1e4;       // cursor in canvas space (far away = inert)
    var running = false, raf = 0;

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = wrap.clientWidth; H = wrap.clientHeight;
      GAP = W < 720 ? 36 : 26;      // fewer lines on small screens
      canvas.width = Math.max(1, W * dpr);
      canvas.height = Math.max(1, H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (REDUCE) draw();           // static mode: re-render the single frame
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      t += 0.008;
      var cols = Math.ceil(W / GAP) + 1;
      var R2 = 170 * 170;           // cursor influence radius²
      for (var i = 0; i < cols; i++) {
        var x0 = i * GAP, lit = 0;
        ctx.beginPath();
        for (var y = 0; y <= H + STEP; y += STEP) {
          // ambient fabric drift: two crossed sine bands, cheap pseudo-noise
          var amb = Math.sin(y * 0.012 + t * 2 + x0 * 0.05) * 4 +
                    Math.sin(y * 0.004 - t + x0 * 0.013) * 8;
          // radial push away from the cursor, gaussian falloff
          var dx = x0 - mx, dy = y - my, d2 = dx * dx + dy * dy;
          var push = 0;
          if (d2 < R2 * 4) push = (dx / Math.sqrt(d2 + 40)) * 62 * Math.exp(-d2 / R2);
          if (push > lit) lit = push; else if (-push > lit) lit = -push;
          var x = x0 + amb + push;
          if (y === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        // lines brighten where they bend — the field "lights up" under the cursor
        var a = 0.09 + Math.min(0.45, (lit / 62) * 0.45);
        ctx.strokeStyle = 'rgba(183,255,0,' + a.toFixed(3) + ')';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      if (running && !REDUCE) raf = requestAnimationFrame(draw);
    }

    function start() { if (running || REDUCE) return; running = true; raf = requestAnimationFrame(draw); }
    function stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; }

    if (FINE && !REDUCE) {
      window.addEventListener('pointermove', function (e) {
        var r = canvas.getBoundingClientRect();
        mx = e.clientX - r.left; my = e.clientY - r.top;
      }, { passive: true });
      window.addEventListener('pointerout', function (e) {
        if (!e.relatedTarget) { mx = -1e4; my = -1e4; }
      });
    }

    window.addEventListener('resize', resize, { passive: true });
    resize();

    if (REDUCE) { draw(); return; }  // one static frame, done
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (en) { en.isIntersecting ? start() : stop(); });
      }, { rootMargin: '80px' }).observe(canvas);
    } else start();
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else if (canvas.getBoundingClientRect().top < window.innerHeight) start();
    });
  }

  function init() {
    if (window.HUB_LITE) return;   // lite mode: skip the ambient contour canvas on weak devices
    try {
      var list = document.querySelectorAll('canvas[data-hub-contour]');
      Array.prototype.forEach.call(list, attach);
    } catch (e) { /* fail safe */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
